import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@proptr/api-client/react';
import {
  AlertIcon,
  AuthSplitShell,
} from '../components/layout/AuthSplitShell.js';
import { WebsiteAnalysisStep } from '../components/onboarding/WebsiteAnalysisStep.js';
import type { OnboardingStepMeta } from '../components/onboarding/OnboardingShell.js';
import { client } from '../main.js';

// The API surfaces zod validation failures as a raw serialized issue array
// ([{"code":"too_small",...}]). That's useless to a user staring at a dead
// magic link, so anything that doesn't read as a sentence gets replaced.
function friendlyError(err: unknown): string {
  const raw = (err as Error)?.message ?? '';
  const looksMachineGenerated =
    !raw || raw.trimStart().startsWith('[') || raw.trimStart().startsWith('{');
  return looksMachineGenerated
    ? 'This verification link is invalid or has expired. Request a new one by signing in.'
    : raw;
}

// Wizard step registry. Add new steps here as they're built — the shell
// reads `shortLabel` for the progress strip.
// Parked steps, re-add to restore:
//   { id: 'role', shortLabel: 'About you' }        (RoleListingsStep —
//     manager/agent split + agent-lead handoff; everyone is treated as a
//     manager while it's parked, see the silent save-role below)
//   { id: 'crm-connect', shortLabel: 'CRM' }        (CrmConnectStep —
//     managers connect their CRM later from settings instead)
const STEPS = [
  { id: 'website-analysis', shortLabel: 'Brand basics' },
] as const satisfies readonly OnboardingStepMeta[];

type State =
  | { kind: 'verifying' }
  | { kind: 'wizard'; pendingCompanyWebsite: string | null }
  | { kind: 'error'; message: string };

export function OnboardingPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, refresh } = useAuth();
  const [state, setState] = useState<State>(
    token ? { kind: 'verifying' } : { kind: 'verifying' },
  );

  // With the role step parked, record the manager defaults it used to
  // collect (signupKind manager, wantsListings true) as soon as the
  // wizard opens. Fire-and-forget: the enterprise doc keeps the same
  // shape it had when the step existed, and a failure here never blocks
  // the wizard — /complete works regardless.
  const roleSaved = useRef(false);
  useEffect(() => {
    if (state.kind !== 'wizard' || roleSaved.current) return;
    roleSaved.current = true;
    void client.onboarding
      .saveRole({ kind: 'manager', wantsListings: true })
      .catch(() => {});
  }, [state.kind]);

  // StrictMode double-invokes effects in dev — guard so we don't burn the
  // single-shot token on the second pass.
  const consumed = useRef(false);
  // refresh is a fresh closure per AuthProvider render; reading it through
  // a ref keeps this effect dep'd on `token` only and avoids a cancel race.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    // No token in the URL — assume the user already verified earlier and is
    // resuming. Just check the session.
    if (!token) {
      void (async () => {
        try {
          const me = await client.auth.me();
          await refreshRef.current();
          setState({
            kind: 'wizard',
            // /api/auth/me doesn't currently return pendingCompanyWebsite —
            // the wizard's first POST will read it from the user doc on the
            // server side anyway, so passing null here is fine.
            pendingCompanyWebsite: null,
          });
          // Touch `me` so eslint doesn't complain about unused.
          void me;
        } catch {
          setState({
            kind: 'error',
            message:
              'You need to be signed in to continue onboarding. Please sign in to resume.',
          });
        }
      })();
      return;
    }

    void (async () => {
      try {
        const res = await client.auth.verifySignup({ token });
        await refreshRef.current();
        setState({
          kind: 'wizard',
          pendingCompanyWebsite: res.pendingCompanyWebsite,
        });
      } catch (err) {
        setState({ kind: 'error', message: friendlyError(err) });
      }
    })();
  }, [token]);

  if (state.kind === 'verifying') {
    return (
      <AuthSplitShell
        eyebrow="Almost in"
        headline={
          <>
            Confirming it&rsquo;s you.
            <br />
            <em>One moment.</em>
          </>
        }
        description={
          <>
            <strong>We&rsquo;re activating your account.</strong> Next you&rsquo;ll
            confirm your brokerage&rsquo;s basics and your proptree goes live.
          </>
        }
      >
        <div className="auth-form">
          <h1>Verifying your email</h1>
          <p className="auth-subtitle">Confirming your magic link.</p>

          <div className="auth-skeleton auth-skeleton--label" />
          <div className="auth-skeleton" style={{ marginBottom: 16 }} />
          <div className="auth-skeleton auth-skeleton--label" />
          <div className="auth-skeleton" />
        </div>
      </AuthSplitShell>
    );
  }

  if (state.kind === 'error') {
    // No token + not signed in → bounce to login. Token error → render the
    // explanation in the auth shell.
    if (!token && !user) return <Navigate to="/login" replace />;
    return (
      <AuthSplitShell
        eyebrow="Verification"
        headline={
          <>
            That link&rsquo;s spent.
            <br />
            <em>Easy to replace.</em>
          </>
        }
        description={
          <>
            <strong>Links are single-use and expire in 24 hours.</strong> Sign
            in and we&rsquo;ll pick onboarding back up.
          </>
        }
      >
        <div className="auth-form">
          <h1>Link unusable</h1>
          <p className="auth-subtitle">
            The verification token is missing, expired, or already used.
          </p>

          <div className="auth-banner" role="alert">
            <AlertIcon />
            <p>{state.message}</p>
          </div>

          <a
            href="/login"
            className="auth-primary-button auth-login-button"
            style={{ textDecoration: 'none' }}
          >
            Go to sign in
          </a>
        </div>
      </AuthSplitShell>
    );
  }

  // Single-step wizard while role + CRM are parked: brand basics is both
  // the first and last step.
  return (
    <WebsiteAnalysisStep
      steps={STEPS}
      stepId="website-analysis"
      fallbackWebsite={state.pendingCompanyWebsite}
      onContinue={() => {
        // Mark onboarding done and drop the user into the dashboard.
        // `complete` is idempotent; if it fails we navigate anyway and
        // the dashboard's pending-state handling brings the user back
        // to retry.
        //
        // A full page load, not a router navigate: the app booted on the
        // magic link while the agency import was still running, so the
        // session (no logo yet) and Testing mode's dataset (static
        // fixture) are both stale. Reloading picks up the saved branding
        // and the now-finished import.
        void client.onboarding
          .complete()
          .catch(() => {})
          .finally(() => window.location.assign('/'));
      }}
    />
  );
}
